import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import api from "../../lib/api";

export function SelfAssessmentSettingsTab() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editCode, setEditCode] = useState("");
  const [saving, setSaving] = useState(false);
  const appUrl = window.location.origin;

  const fetchCodes = async () => {
    try {
      const res = await api.get("/self-assessment/codes");
      setStudents(res.data);
    } catch { toast.error("Failed to load codes"); }
    setLoading(false);
  };

  useEffect(() => { fetchCodes(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/self-assessment/generate-codes");
      toast.success(`Generated ${res.data.generated} new access codes (birthday-based when available)`);
      setStudents(res.data.students);
    } catch { toast.error("Failed to generate codes"); }
    setGenerating(false);
  };

  const handleUpdateCode = async (studentId) => {
    if (!editCode.trim() || editCode.trim().length < 4) {
      toast.error("Code must be at least 4 characters");
      return;
    }
    setSaving(true);
    try {
      await api.put("/self-assessment/update-code", { student_id: studentId, code: editCode.trim() });
      toast.success("Access code updated");
      setEditingId(null);
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update code");
    }
    setSaving(false);
  };

  const handlePrint = () => {
    const sorted = students
      .filter(s => s.self_assessment_code)
      .sort((a, b) => (a.preferred_name || a.first_name || "").localeCompare(b.preferred_name || b.first_name || ""));
    const rows = sorted.map(s =>
      `<tr><td style="border:1px solid #ddd;padding:8px;font-size:14px">${s.preferred_name || s.first_name} ${s.last_name}</td><td style="border:1px solid #ddd;padding:8px;font-size:20px;font-family:monospace;font-weight:bold;text-align:center;letter-spacing:4px">${s.self_assessment_code}</td></tr>`
    ).join("");
    const pw = window.open("", "_blank");
    pw.document.write(`<html><head><title>Student Access Codes</title><style>body{font-family:Arial,sans-serif}table{border-collapse:collapse;width:100%;max-width:500px;margin:20px auto}th{background:#00305E;color:white;padding:10px;text-align:left}@media print{.no-print{display:none}}</style></head><body><h2 style="text-align:center;color:#00305E">Student Self-Assessment Access Codes</h2><p style="text-align:center;color:#666">Go to: <strong>${appUrl}/self-assess</strong> or use the main login page</p><table><thead><tr><th>Student</th><th style="text-align:center">Access Code</th></tr></thead><tbody>${rows}</tbody></table><p style="text-align:center;color:#999;font-size:12px;margin-top:20px">Keep this sheet secure. Each student should only see their own code.</p></body></html>`);
    pw.document.close();
    pw.print();
  };

  const withCodes = students.filter(s => s.self_assessment_code);
  const withoutCodes = students.filter(s => !s.self_assessment_code);

  if (loading) return <p className="text-center py-8 text-muted-foreground">Loading...</p>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Student Self-Assessment Portal</CardTitle>
          <CardDescription>
            Students can rate themselves on the same 6 categories teachers use. Access codes default to birthday (MMDDYY). You can reassign any code below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg border bg-blue-50">
            <p className="font-medium text-sm text-blue-900">Student Portal URL</p>
            <code className="text-lg font-mono text-blue-700 block mt-1" data-testid="self-assess-url">{appUrl}/self-assess</code>
            <p className="text-xs text-blue-600 mt-1">Students can also use the main login page &rarr; "Student Self-Assessment" tab.</p>
          </div>

          <div className="flex gap-3">
            <Button onClick={handleGenerate} disabled={generating} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="generate-codes-btn">
              {generating ? "Generating..." : withoutCodes.length > 0 ? `Generate Codes (${withoutCodes.length} students need codes)` : "Generate Codes"}
            </Button>
            {withCodes.length > 0 && (
              <Button variant="outline" onClick={handlePrint} data-testid="print-codes-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/></svg>
                Print Code Sheet
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {withCodes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Student Access Codes ({withCodes.length})</CardTitle>
            <CardDescription>Click any code to reassign it. Defaults are based on birthday (MMDDYY).</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="codes-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-3 py-2 text-left font-semibold">Student</th>
                    <th className="px-3 py-2 text-center font-semibold">Access Code</th>
                    <th className="px-3 py-2 text-center font-semibold w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {withCodes.sort((a, b) => (a.preferred_name || a.first_name).localeCompare(b.preferred_name || b.first_name)).map(s => (
                    <tr key={s.id} className="border-b hover:bg-gray-50" data-testid={`code-row-${s.id}`}>
                      <td className="px-3 py-2">{s.preferred_name || s.first_name} {s.last_name}</td>
                      <td className="px-3 py-2 text-center">
                        {editingId === s.id ? (
                          <div className="flex items-center justify-center gap-2">
                            <Input
                              value={editCode}
                              onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                              onKeyDown={(e) => e.key === "Enter" && handleUpdateCode(s.id)}
                              className="w-32 text-center font-mono text-lg tracking-widest uppercase h-9"
                              maxLength={8}
                              autoFocus
                              data-testid={`edit-code-input-${s.id}`}
                            />
                            <Button size="sm" onClick={() => handleUpdateCode(s.id)} disabled={saving} className="bg-[#00305E] h-9" data-testid={`save-code-btn-${s.id}`}>
                              {saving ? "..." : "Save"}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-9">Cancel</Button>
                          </div>
                        ) : (
                          <span className="font-mono text-lg font-bold tracking-widest text-[#00305E]">{s.self_assessment_code}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {editingId !== s.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingId(s.id); setEditCode(s.self_assessment_code); }}
                            className="text-xs"
                            data-testid={`edit-code-btn-${s.id}`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
